import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import type { LocaleSchema, MobileMiniWorkout } from '../../api/generated';
import { getEntityTranslations, getTranslationSchema } from '../../services/TranslationService';
import { MAX_WORKOUT_NAME_LENGTH, renameWorkoutDay } from '../../services/WorkoutDayNameService';
import { getErrorMessage } from '../../enums/ErrorMessages';

export interface RenameWorkoutDayDialogProps {
    open: boolean;
    onClose: () => void;
    programId: number;
    /** Any week's copy of the day — the rename applies to every week. */
    workout: MobileMiniWorkout | null;
    /** 1-based tab position, for the placeholder ("אימון 2"). */
    position: number;
    onRenamed: (nameHeb: string) => void;
    onError: (message: string) => void;
}

/**
 * Rename one workout day of a trainee's program, in Hebrew and in every other enabled language.
 *
 * The languages come from the server's translation schema, so a new locale shows up here with no
 * change. A language left empty is cleared on save: the old name's translation ("Тренировка 1")
 * would otherwise keep showing next to the new Hebrew one, and the trainee app falls back instead.
 */
const RenameWorkoutDayDialog = ({
    open,
    onClose,
    programId,
    workout,
    position,
    onRenamed,
    onError,
}: RenameWorkoutDayDialogProps) => {
    const [nameHeb, setNameHeb] = useState('');
    const [translations, setTranslations] = useState<Record<string, string>>({});
    const [locales, setLocales] = useState<LocaleSchema[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !workout?.id) return;
        let cancelled = false;
        // A placeholder name ("אימון B") is not the coach's — start blank rather than prefill it.
        const custom = Boolean(workout.customName);
        setNameHeb(custom ? workout.nameHeb ?? '' : '');
        setTranslations({});
        setLoading(true);

        Promise.all([getTranslationSchema('WORKOUT'), getEntityTranslations('WORKOUT', workout.id)])
            .then(([schema, current]) => {
                if (cancelled) return;
                const editable = schema.ok
                    ? (schema.data.locales ?? []).filter(l => l.code && l.code !== schema.data.canonicalLocale)
                    : [];
                setLocales(editable);
                if (custom && current.ok) {
                    // Only the locales this dialog has a field for. The stored translations include
                    // the canonical (Hebrew) row too, and sending it back is refused by the server
                    // ("The he name goes in nameHeb") — so every day renamed once could never be
                    // renamed again.
                    const editableCodes = new Set(editable.map(l => l.code));
                    const prefill: Record<string, string> = {};
                    Object.entries(current.data.translations ?? {}).forEach(([locale, fields]) => {
                        const value = fields?.name?.value;
                        if (value && editableCodes.has(locale)) prefill[locale] = value;
                    });
                    setTranslations(prefill);
                }
            })
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
    }, [open, workout]);

    const trimmed = nameHeb.trim();
    const tooLong =
        trimmed.length > MAX_WORKOUT_NAME_LENGTH ||
        Object.values(translations).some(v => v.trim().length > MAX_WORKOUT_NAME_LENGTH);
    const canSave = Boolean(trimmed) && !tooLong && !saving && !loading && Boolean(workout?.groupUuid);

    const handleSave = async () => {
        if (!canSave || !workout?.groupUuid) return;
        setSaving(true);
        try {
            await renameWorkoutDay({ programId, groupUuid: workout.groupUuid, nameHeb: trimmed, translations });
            onRenamed(trimmed);
        } catch (e) {
            const code = (e as { response?: { data?: { errorCode?: number } } }).response?.data?.errorCode;
            onError(code ? getErrorMessage(code) : 'שגיאה בשינוי שם האימון');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} dir="rtl" fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 600, textAlign: 'center' }}>שינוי שם יום אימון</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                    השם יופיע למתאמן באפליקציה ויחול על יום האימון בכל השבועות.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label="שם בעברית"
                        value={nameHeb}
                        onChange={e => setNameHeb(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSave()}
                        placeholder={`אימון ${position}`}
                        size="small"
                        fullWidth
                        autoFocus
                        inputProps={{ maxLength: MAX_WORKOUT_NAME_LENGTH, 'data-testid': 'rename-day-he' }}
                    />
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : (
                        locales.map(locale => (
                            <TextField
                                key={locale.code}
                                label={locale.displayName || locale.code}
                                value={translations[locale.code as string] ?? ''}
                                onChange={e =>
                                    setTranslations(prev => ({ ...prev, [locale.code as string]: e.target.value }))
                                }
                                size="small"
                                fullWidth
                                dir={locale.rtl ? 'rtl' : 'ltr'}
                                inputProps={{
                                    maxLength: MAX_WORKOUT_NAME_LENGTH,
                                    'data-testid': `rename-day-${locale.code}`,
                                }}
                            />
                        ))
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button onClick={onClose}>ביטול</Button>
                <Button onClick={handleSave} variant="contained" disabled={!canSave}>
                    {saving ? <CircularProgress size={18} color="inherit" /> : 'שמור'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RenameWorkoutDayDialog;
